import { cssVars } from "../css-vars";
import { cn } from "../lib/cn";
import {
  AxisLabels,
  CHART_TONES,
  ChartLegend,
  ChartTableFallback,
  ChartTooltip,
  SvgFrame,
  defaultFormat,
  niceMax,
  useChartTooltip,
  type ChartTone,
} from "./chart-base";

const W = 600;
const H = 220;
const PAD = 6;
const GRID_LINES = 4;

export interface LineSeries {
  label: string;
  tone: ChartTone;
  values: readonly number[];
  /** Dashed stroke (e.g. "Previous" period) — pattern differs, not just colour. */
  dashed?: boolean;
}

export interface LineChartProps {
  title: string;
  description?: string;
  /** One label per x position (used for tooltips and the data table). */
  labels: readonly string[];
  series: readonly LineSeries[];
  axisLabels?: readonly string[];
  max?: number;
  formatValue?: (value: number) => string;
  showLegend?: boolean;
  className?: string;
}

/** Multi-series line chart (current vs previous). Each x position is a focus stop. */
export function LineChart({ title, description, labels, series, axisLabels = [], max, formatValue = defaultFormat, showLegend = true, className }: LineChartProps) {
  const { tip, show, hide } = useChartTooltip();
  const top = max ?? niceMax(series.flatMap((s) => s.values));
  const count = labels.length;
  const x = (i: number) => PAD + (i * (W - 2 * PAD)) / Math.max(count - 1, 1);
  const y = (v: number) => H - PAD - (v / top) * (H - 2 * PAD);
  const primary = series[0];
  const describe = (i: number) =>
    `${labels[i] ?? ""}: ${series.map((s) => `${formatValue(s.values[i] ?? 0)} ${s.label.toLowerCase()}`).join(", ")}`;

  return (
    <div>
      {showLegend ? <ChartLegend className="mb-3" items={series.map((s) => ({ label: s.label, tone: s.tone, shape: "line" as const }))} /> : null}
      <div className={cn("relative", className ?? "h-60")}>
        <SvgFrame title={title} description={description} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full" onMouseLeave={hide}>
          {Array.from({ length: GRID_LINES + 1 }, (_, g) => (
            <line key={g} x1={0} x2={W} y1={y((top / GRID_LINES) * g)} y2={y((top / GRID_LINES) * g)} strokeWidth={1} vectorEffect="non-scaling-stroke" className="stroke-line" />
          ))}
          {series.map((s) => (
            <polyline
              key={s.label}
              points={s.values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ")}
              fill="none"
              strokeWidth={s.dashed ? 2 : 2.5}
              strokeDasharray={s.dashed ? "4 4" : undefined}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              className={CHART_TONES[s.tone].stroke}
            />
          ))}
          {labels.map((label, i) => {
            const text = describe(i);
            const reveal = () => {
              show({ x: (x(i) / W) * 100, y: (y(primary?.values[i] ?? 0) / H) * 100, text, index: i });
            };
            return (
              <rect
                key={`${label}-${i}`}
                x={x(i) - W / count / 2}
                y={0}
                width={W / count}
                height={H}
                fill="transparent"
                tabIndex={0}
                role="img"
                aria-label={text}
                className="outline-none focus-visible:fill-primary/10"
                onMouseEnter={reveal}
                onFocus={reveal}
                onBlur={hide}
              />
            );
          })}
        </SvgFrame>
        {/* Active dot drawn in HTML so it stays round under preserveAspectRatio="none". */}
        {tip && primary ? (
          <span
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute left-[var(--geo-x)] top-[var(--geo-y)] size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-pill ring-2 ring-surface",
              CHART_TONES[primary.tone].swatch,
            )}
            style={cssVars({ "--geo-x": `${tip.x}%`, "--geo-y": `${tip.y}%` })}
          />
        ) : null}
        <ChartTooltip tip={tip} />
      </div>
      <AxisLabels labels={axisLabels} />
      <ChartTableFallback
        caption={title}
        table={{ columns: ["", ...series.map((s) => s.label)], rows: labels.map((label, i) => [label, ...series.map((s) => formatValue(s.values[i] ?? 0))]) }}
      />
    </div>
  );
}
